import type { ReceiptStyleOptions } from "./receiptGenerator"
import { EscPosEncoder } from "./escPosEncoder"
import {
  HardwareBridgeFeatureUnsupportedError,
  PrintOutcomeUnknownError,
  PrintReconcileRequiredError,
} from "./hardwareBridgeErrors"
import { generatePlainTextReceipt } from "./receiptGenerator"
import { ReceiptRasterCommandRecorder } from "./receiptRasterRenderer"

const WS_URL = "ws://127.0.0.1:12212/printer"
const PRINTER_MAPPINGS_URL = "http://127.0.0.1:12212/system/printer-mappings.json"
const LOCAL_PRINT_DEVICE_CONTEXT_URL = "http://127.0.0.1:12212/system/local-print-device-context.json"
const LOCAL_PRINT_DEVICE_ACTIVATION_URL
  = "http://127.0.0.1:12212/system/local-print-device-context/activation.json"
const PRINT_RESULT_TIMEOUT_MS = 30_000
const CAPABILITY_TIMEOUT_MS = 3_000

export type PrinterType = string

export interface PrintJob {
  id?: string
  type: PrinterType
  url?: string // For INVOICE
  raw_content?: string // For RECEIPT (Base64)
}

export interface PrintResult {
  id?: string
  success: boolean
  message?: string
  printerName?: string
  state?: "SUCCESS" | "FAILED" | "RECONCILE_REQUIRED" | string
}

export interface LocalPrinterMapping {
  type: string
  printerName?: string | null
  available: boolean
}

export interface LocalPrintDeviceContext {
  deviceId: string
  shopId: string
  activationTaskId: number | null
  activationToken?: string
}

interface PendingPrintJob {
  resolve: (value: boolean) => void
  reject: (reason?: unknown) => void
  timeout: ReturnType<typeof setTimeout>
  socket: WebSocket
}

export class HardwareBridge {
  private ws: WebSocket | null = null
  private connectionPromise: Promise<WebSocket> | null = null
  private readonly pendingJobs = new Map<string, PendingPrintJob>()

  constructor(private readonly printResultTimeoutMs = PRINT_RESULT_TIMEOUT_MS) {}

  /**
   * Ensure connection exists
   */
  private async connect(): Promise<WebSocket> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return this.ws
    }

    if (this.connectionPromise) {
      return this.connectionPromise
    }

    const connection = new Promise<WebSocket>((resolve, reject) => {
      try {
        const socket = new WebSocket(WS_URL)
        let opened = false
        this.ws = socket

        socket.onopen = () => {
          console.log("[HardwareBridge] Connected")
          opened = true
          resolve(socket)
        }

        socket.onmessage = event => this.handleMessage(socket, event)

        socket.onclose = () => {
          console.log("[HardwareBridge] Disconnected")
          if (!opened) {
            reject(new Error("本地打印助手连接已关闭"))
          }
          if (this.ws === socket) {
            this.ws = null
          }
          this.rejectPendingJobs(
            socket,
            id => new PrintOutcomeUnknownError(id, "本地打印助手连接已断开，打印结果未知"),
          )
        }

        socket.onerror = (error) => {
          console.error("[HardwareBridge] Error", error)
          if (!opened) {
            reject(new Error("无法连接本地打印助手"))
          }
        }
      } catch (e) {
        reject(e)
      }
    })

    this.connectionPromise = connection.finally(() => {
      this.connectionPromise = null
    })
    return this.connectionPromise
  }

  private handleMessage(socket: WebSocket, event: MessageEvent) {
    if (typeof event.data !== "string") return

    let result: PrintResult
    try {
      result = JSON.parse(event.data) as PrintResult
    } catch {
      return
    }

    if (!result.id || typeof result.success !== "boolean") return
    const pending = this.pendingJobs.get(result.id)
    if (!pending || pending.socket !== socket) return

    clearTimeout(pending.timeout)
    this.pendingJobs.delete(result.id)
    if (result.state === "RECONCILE_REQUIRED") {
      pending.reject(new PrintReconcileRequiredError(
        result.id,
        result.message || "本地打印提交结果需要人工核对",
      ))
    } else if (result.success) {
      pending.resolve(true)
    } else {
      pending.reject(new Error(result.message || "本地打印失败"))
    }
  }

  private rejectPendingJobs(socket: WebSocket, createError: (id: string) => Error) {
    for (const [id, pending] of this.pendingJobs) {
      if (pending.socket !== socket) continue
      clearTimeout(pending.timeout)
      this.pendingJobs.delete(id)
      pending.reject(createError(id))
    }
  }

  private createPrintJobId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
    return `print-${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  /**
   * Send print job
   */
  async print(job: PrintJob): Promise<boolean> {
    try {
      const ws = await this.connect()
      const id = job.id || this.createPrintJobId()
      if (this.pendingJobs.has(id)) {
        throw new Error(`本地打印任务正在处理中: ${id}`)
      }

      return await new Promise<boolean>((resolve, reject) => {
        const timeout = setTimeout(() => {
          this.pendingJobs.delete(id)
          reject(new PrintOutcomeUnknownError(id, "等待本地打印结果超时，打印结果未知"))
        }, this.printResultTimeoutMs)
        this.pendingJobs.set(id, { resolve, reject, timeout, socket: ws })

        try {
          ws.send(JSON.stringify({ ...job, id }))
        } catch (error) {
          clearTimeout(timeout)
          this.pendingJobs.delete(id)
          reject(new PrintOutcomeUnknownError(
            id,
            `发送本地打印任务失败，打印结果待核对: ${error instanceof Error ? error.message : String(error)}`,
          ))
        }
      })
    } catch (e) {
      console.error("[HardwareBridge] Print failed", e)
      throw e
    }
  }

  async canPrint() {
    try {
      await this.connect()
      return true
    } catch {
      return false
    }
  }

  async getPrinterMappings(): Promise<LocalPrinterMapping[]> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), CAPABILITY_TIMEOUT_MS)
    try {
      const response = await fetch(PRINTER_MAPPINGS_URL, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      })
      if (!response.ok) {
        throw new Error(`本地打印助手不支持打印机映射查询: HTTP ${response.status}`)
      }
      const mappings = await response.json()
      if (!Array.isArray(mappings)) {
        throw new TypeError("本地打印助手返回的打印机映射格式无效")
      }
      const valid = mappings.filter(mapping => mapping
        && typeof mapping.type === "string" && mapping.type.trim()
        && typeof mapping.available === "boolean")
      // 保留旧版跳过无效项的兼容性；损坏响应不能作为“没有可用打印机”的证据。
      if (valid.length !== mappings.length && !valid.some(mapping => mapping.available)) {
        throw new TypeError("本地打印助手返回的打印机映射格式无效")
      }
      return valid
        .map(mapping => ({
          type: mapping.type.trim(),
          printerName: typeof mapping.printerName === "string" ? mapping.printerName : null,
          available: mapping.available,
        }))
    } finally {
      clearTimeout(timeout)
    }
  }

  async getLocalPrintDeviceContext(shopId: string): Promise<LocalPrintDeviceContext> {
    const normalizedShopId = this.normalizeShopId(shopId)
    return this.fetchLocalPrintDeviceContext(
      `${LOCAL_PRINT_DEVICE_CONTEXT_URL}?shopId=${encodeURIComponent(normalizedShopId)}`,
      { method: "GET", cache: "no-store" },
      normalizedShopId,
    )
  }

  async activateLocalPrintDeviceContext(
    shopId: string,
    activationTaskId: number,
    activationToken: string,
  ): Promise<LocalPrintDeviceContext> {
    const normalizedShopId = this.normalizeShopId(shopId)
    if (!Number.isSafeInteger(activationTaskId) || activationTaskId < 0) {
      throw new TypeError("本地打印设备接入游标无效")
    }
    const normalizedActivationToken = this.normalizeActivationToken(activationToken)
    return this.fetchLocalPrintDeviceContext(LOCAL_PRINT_DEVICE_ACTIVATION_URL, {
      method: "POST",
      cache: "no-store",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        shopId: normalizedShopId,
        activationTaskId,
        activationToken: normalizedActivationToken,
      }),
    }, normalizedShopId)
  }

  private async fetchLocalPrintDeviceContext(
    url: string,
    init: RequestInit,
    expectedShopId: string,
  ): Promise<LocalPrintDeviceContext> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), CAPABILITY_TIMEOUT_MS)
    try {
      const response = await fetch(url, { ...init, signal: controller.signal })
      if (!response.ok) {
        if (response.status === 404 || response.status === 405) {
          throw new HardwareBridgeFeatureUnsupportedError(
            `本地打印助手版本不支持设备身份: HTTP ${response.status}`,
          )
        }
        throw new Error(`本地打印助手设备身份不可用: HTTP ${response.status}`)
      }
      const value = await response.json() as Partial<LocalPrintDeviceContext>
      const cursorValid = value.activationTaskId == null
        || (Number.isSafeInteger(value.activationTaskId) && Number(value.activationTaskId) >= 0)
      const activationTokenValid = value.activationToken == null
        || (typeof value.activationToken === "string"
          && /^[a-z0-9_-]{20,256}$/i.test(value.activationToken))
      if (typeof value.deviceId !== "string"
        || !/^[a-z0-9._:-]{1,128}$/i.test(value.deviceId)
        || value.shopId !== expectedShopId
        || !cursorValid
        || !activationTokenValid) {
        throw new TypeError("本地打印助手返回的设备身份格式无效")
      }
      return {
        deviceId: value.deviceId,
        shopId: value.shopId,
        activationTaskId: value.activationTaskId ?? null,
        ...(value.activationToken ? { activationToken: value.activationToken } : {}),
      }
    } finally {
      clearTimeout(timeout)
    }
  }

  private normalizeShopId(shopId: string) {
    const normalized = shopId.trim()
    if (!/^\d{1,32}$/.test(normalized)) {
      throw new TypeError("本地打印门店标识无效")
    }
    return normalized
  }

  private normalizeActivationToken(activationToken: string) {
    const normalized = activationToken.trim()
    if (!/^[a-z0-9_-]{20,256}$/i.test(normalized)) {
      throw new TypeError("本地打印设备激活凭据无效")
    }
    return normalized
  }

  /**
   * Helper for printing receipts via ESC/POS
   */
  async printReceipt(
    encoderCallback: (_encoder: EscPosEncoder) => void,
    id?: string,
    printerType: PrinterType = "RECEIPT",
    style?: ReceiptStyleOptions,
  ) {
    const encoder = new EscPosEncoder()
    if (style?.renderMode === "raster" && style.raster) {
      try {
        const recorder = new ReceiptRasterCommandRecorder()
        encoderCallback(recorder as unknown as EscPosEncoder)
        recorder.renderTo(encoder, style.raster)
      } catch (error) {
        console.warn("Raster receipt rendering failed, falling back to native text", error)
        encoder.buffer.length = 0
        encoderCallback(encoder)
      }
    } else {
      encoderCallback(encoder)
    }
    const base64 = encoder.toBase64()
    return this.print({
      id,
      type: printerType,
      raw_content: base64,
    })
  }

  async printPlainText(
    content: string,
    style?: ReceiptStyleOptions,
    id?: string,
    printerType: PrinterType = "RECEIPT",
  ) {
    return this.printReceipt(generatePlainTextReceipt(content, style), id, printerType, style)
  }

  /**
   * Helper for printing invoices (PDF/Image)
   */
  async printInvoice(url: string, id?: string) {
    return this.print({
      id,
      type: "INVOICE",
      url,
    })
  }
}

export const hardwareBridge = new HardwareBridge()
